import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { useOrganization } from './OrganizationContext';

const PermissionsContext = createContext();

/**
 * Hook to access permissions for the current user in the selected organisation.
 *
 * @example
 * const { hasPermission, can, permissions, loading } = usePermissions();
 *
 * // Check a specific permission
 * if (hasPermission('flow:create')) { ... }
 *
 * // Check multiple permissions (any)
 * if (can.createFlow) { ... }
 */
export const usePermissions = () => {
  const context = useContext(PermissionsContext);
  if (!context) {
    throw new Error('usePermissions must be used within PermissionsProvider');
  }
  return context;
};

export const PermissionsProvider = ({ children }) => {
  const { user, authenticatedFetch } = useAuth();
  const { selectedOrganization } = useOrganization();

  const [permissions, setPermissions] = useState(new Set());
  const [isAppAdmin, setIsAppAdmin] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Cache permissions per org to avoid refetching on org switch
  const [permissionsCache, setPermissionsCache] = useState({});

  const API_URL = process.env.REACT_APP_API_URL;

  /**
   * Load system admin status for the current user.
   * This is global and not org-scoped.
   */
  const loadSystemAdminStatus = useCallback(async () => {
    if (!user) {
      setIsAppAdmin(false);
      return;
    }

    try {
      const response = await authenticatedFetch(
        `${API_URL}/api/me/is-system-admin`
      );

      if (response.ok) {
        const isAdmin = await response.json();
        setIsAppAdmin(isAdmin);
      } else {
        setIsAppAdmin(false);
      }
    } catch (err) {
      console.error('Error checking system admin status:', err);
      setIsAppAdmin(false);
    }
  }, [user, authenticatedFetch, API_URL]);

  /**
   * Load permissions for the current user in the selected organisation.
   */
  const loadPermissions = useCallback(async (orgId) => {
    if (!orgId || !user) {
      setPermissions(new Set());
      return;
    }

    // Check cache first
    if (permissionsCache[orgId]) {
      setPermissions(new Set(permissionsCache[orgId]));
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await authenticatedFetch(
        `${API_URL}/api/organisations/${orgId}/my-permissions`
      );

      if (!response.ok) {
        throw new Error('Failed to load permissions');
      }

      const permissionCodes = await response.json();
      const permissionSet = new Set(permissionCodes);

      // Cache the permissions
      setPermissionsCache(prev => ({
        ...prev,
        [orgId]: permissionCodes
      }));

      setPermissions(permissionSet);
    } catch (err) {
      console.error('Error loading permissions:', err);
      setError(err.message);
      // On error, default to empty permissions (fail-safe)
      setPermissions(new Set());
    } finally {
      setLoading(false);
    }
  }, [user, authenticatedFetch, API_URL, permissionsCache]);

  /**
   * Check if the user has a specific permission.
   * @param {string} permissionCode - The permission code to check (e.g., 'flow:create')
   * @returns {boolean} True if the user has the permission
   */
  const hasPermission = useCallback((permissionCode) => {
    return permissions.has(permissionCode);
  }, [permissions]);

  /**
   * Check if the user has any of the specified permissions.
   * @param  {...string} permissionCodes - The permission codes to check
   * @returns {boolean} True if the user has any of the permissions
   */
  const hasAnyPermission = useCallback((...permissionCodes) => {
    return permissionCodes.some(code => hasPermission(code));
  }, [hasPermission]);

  /**
   * Check if the user has all of the specified permissions.
   * @param  {...string} permissionCodes - The permission codes to check
   * @returns {boolean} True if the user has all of the permissions
   */
  const hasAllPermissions = useCallback((...permissionCodes) => {
    return permissionCodes.every(code => hasPermission(code));
  }, [hasPermission]);

  /**
   * Clear the permissions cache (e.g., after role/permission changes).
   */
  const clearCache = useCallback(() => {
    setPermissionsCache({});
    if (selectedOrganization) {
      loadPermissions(selectedOrganization.id);
    }
  }, [selectedOrganization, loadPermissions]);

  /**
   * Refresh permissions for the current organisation.
   */
  const refreshPermissions = useCallback(() => {
    if (selectedOrganization) {
      // Clear cache for this org and reload
      setPermissionsCache(prev => {
        const newCache = { ...prev };
        delete newCache[selectedOrganization.id];
        return newCache;
      });
      loadPermissions(selectedOrganization.id);
    }
  }, [selectedOrganization, loadPermissions]);

  // Load permissions when organisation changes
  useEffect(() => {
    if (selectedOrganization?.id) {
      loadPermissions(selectedOrganization.id);
    } else {
      setPermissions(new Set());
    }
  }, [selectedOrganization?.id, loadPermissions]);

  // Clear cache and reload system admin status on user change
  useEffect(() => {
    setPermissionsCache({});
    setPermissions(new Set());
    setIsAppAdmin(false);
    loadSystemAdminStatus();
  }, [user?.id, loadSystemAdminStatus]);

  /**
   * Convenience object with commonly used permission checks.
   * Access like: can.createFlow, can.modifyOrg, etc.
   */
  const can = {
    // Organisation
    readOrg: hasPermission('org:read'),
    modifyOrg: hasPermission('org:modify'),
    deleteOrg: hasPermission('org:delete'),
    readMembers: hasPermission('org:members:read'),
    manageMembers: hasPermission('org:members:manage'),
    isOwner: hasPermission('org:owner'),
    transferOwnership: hasPermission('org:transfer_ownership'),

    // Environment
    readEnv: hasPermission('env:read'),
    createEnv: hasPermission('env:create'),
    modifyEnv: hasPermission('env:modify'),
    deleteEnv: hasPermission('env:delete'),
    promoteFlows: hasPermission('env:promote_flows'),

    // Flow
    readFlow: hasPermission('flow:read'),
    createFlow: hasPermission('flow:create'),
    modifyFlow: hasPermission('flow:modify'),
    deleteFlow: hasPermission('flow:delete'),
    publishFlow: hasPermission('flow:publish'),
    discoverFlows: hasPermission('flow:discover'),
    importFlow: hasPermission('flow:import'),
    exportFlow: hasPermission('flow:export'),
    viewVersionHistory: hasPermission('flow:version_history'),

    // WABA
    readWaba: hasPermission('waba:read'),
    modifyWaba: hasPermission('waba:modify'),
    disconnectWaba: hasPermission('waba:disconnect'),

    // Permissions
    readPermissions: hasPermission('permissions:read'),
    modifyPermissions: hasPermission('permissions:modify'),
    readRoles: hasPermission('roles:read'),
    manageRoles: hasPermission('roles:manage'),

    // Audit
    readAudit: hasPermission('audit:read'),

    // App Administration (system permission - global, not org-scoped)
    isAppAdmin: isAppAdmin,
  };

  const value = {
    permissions: Array.from(permissions),
    isAppAdmin,
    loading,
    error,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    can,
    clearCache,
    refreshPermissions,
    refreshSystemAdminStatus: loadSystemAdminStatus,
  };

  return (
    <PermissionsContext.Provider value={value}>
      {children}
    </PermissionsContext.Provider>
  );
};
